import React, { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { Alert, AlertActionCloseButton, AlertGroup, AlertVariant } from '@patternfly/react-core';
import { BellIcon } from '@patternfly/react-icons';
import { NOTIFICATION_SEVERITY } from '@app/model/Enums';
import { Notification as InboxNotification } from '@app/model/Notification';
import { useNotificationStore } from '@app/store/notificationStore';

interface Notification {
    key: number;
    title: string;
    body?: ReactNode;
    variant: AlertVariant;
    visible: boolean;
}

interface NotificationContextProps {
    addNotification: (title: string, variant?: AlertVariant, body?: ReactNode) => void;
}

const NotificationContext = createContext<NotificationContextProps | undefined>(undefined);

/** How long an alert of each variant stays up, in milliseconds. */
const TIMEOUTS: Partial<Record<AlertVariant, number>> = {
    [AlertVariant.danger]: 60000,
    [AlertVariant.warning]: 15000,
    [AlertVariant.info]: 10000,
};
const DEFAULT_TIMEOUT = 5000;

/** Severity as PatternFly paints it. The service has no notion of a "success" notification. */
const SEVERITY_VARIANTS: Record<NOTIFICATION_SEVERITY, AlertVariant> = {
    [NOTIFICATION_SEVERITY.ERROR]: AlertVariant.danger,
    [NOTIFICATION_SEVERITY.WARNING]: AlertVariant.warning,
    [NOTIFICATION_SEVERITY.INFO]: AlertVariant.info,
};

/**
 * How long a notification that has just arrived stays on screen.
 *
 * Longer than the equivalent app alert, because these are unsolicited: an alert answers something
 * the user just did and they are already looking at it, while one of these can land while they are
 * reading something else entirely. Short of a failure, which stays until dismissed - it is the one
 * kind nobody should be able to miss by looking away.
 */
const ARRIVAL_TIMEOUTS: Record<NOTIFICATION_SEVERITY, number> = {
    [NOTIFICATION_SEVERITY.ERROR]: 0, // 0 means it waits to be closed
    [NOTIFICATION_SEVERITY.WARNING]: 20000,
    [NOTIFICATION_SEVERITY.INFO]: 12000,
};

// TODO: keep history of notifications within the session
export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [notifications, setNotifications] = useState<Notification[]>([]);
    // Notifications pushed from the service, shown alongside the alerts above but rendered
    // differently — see the AlertGroup at the bottom.
    const [arrivals, setArrivals] = useState<InboxNotification[]>([]);

    const addNotification = (title: string, variant: AlertVariant = AlertVariant.danger, body?: ReactNode) => {
        const key = Date.now();
        setNotifications((prev) => [...prev, { key, title, variant, visible: true, body: body }]);

        // Removed by key rather than by position. Variants have different lifetimes, so a danger
        // alert raised first and a success alert raised after it do not expire in the order they
        // were added, and dropping the head would take down whichever happened to be oldest.
        setTimeout(() => {
            setNotifications((prev) => prev.filter((notif) => notif.key !== key));
        }, TIMEOUTS[variant] ?? DEFAULT_TIMEOUT);
    };

    const handleClose = (key: number) => {
        setNotifications((prev) => prev.filter((notif) => notif.key !== key));
    };

    /**
     * Shows each notification as it arrives.
     *
     * Subscribed to the store imperatively rather than by rendering off it, because a toast is an
     * event and not a piece of state: this has to fire once per arrival, and a component reading
     * `lastArrival` would re-show it on any later render. zustand calls this synchronously on every
     * write, so two notifications landing in the same tick are still two calls.
     *
     * Gated on arrivalToken moving, which only `receive()` does — so nothing is toasted for the
     * inbox that loads at sign-in, only for what turns up while the user is here.
     */
    useEffect(() =>
        useNotificationStore.subscribe((state, previous) => {
            if (state.arrivalToken > previous.arrivalToken && state.lastArrival) {
                const arrival = state.lastArrival;
                setArrivals((prev) => (prev.some((a) => a.id === arrival.id) ? prev : [...prev, arrival]));
            }
        }),
    []);

    const closeArrival = (id: number) => setArrivals((prev) => prev.filter((a) => a.id !== id));

    return (
        <NotificationContext.Provider value={{ addNotification }}>
            {children}
            <AlertGroup isToast isLiveRegion>
                {notifications.map((notif) => (
                    <Alert key={notif.key} title={notif.title} variant={notif.variant}
                      actionClose={<AlertActionCloseButton onClose={() => handleClose(notif.key)} />}>
                        {notif.body}
                    </Alert>
                ))}

                {/*
                  * The same toast stack, deliberately a different thing to look at. An alert above
                  * is this application talking about what the user just did; one of these is the
                  * service telling them something happened elsewhere, which is the bell's business —
                  * hence the bell icon in place of the status one, and the line naming where it came
                  * from. Without that they read as the same event and the panel looks redundant.
                  */}
                {arrivals.map((arrival) => (
                    <Alert
                        key={`arrival-${arrival.id}`}
                        variant={SEVERITY_VARIANTS[arrival.severity] ?? AlertVariant.info}
                        customIcon={<BellIcon />}
                        title={arrival.title}
                        timeout={ARRIVAL_TIMEOUTS[arrival.severity] ?? 12000}
                        onTimeout={() => closeArrival(arrival.id)}
                        actionClose={<AlertActionCloseButton onClose={() => closeArrival(arrival.id)} />}
                    >
                        {arrival.message}
                        <div
                            style={{
                                marginTop: 'var(--pf-t--global--spacer--sm, 8px)',
                                fontSize: 'var(--pf-t--global--font--size--xs, 12px)',
                                color: 'var(--pf-t--global--text--color--subtle, #6a6e73)',
                            }}
                        >
                            New notification — it is in your bell
                        </div>
                    </Alert>
                ))}
            </AlertGroup>
        </NotificationContext.Provider>
    );
};

export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within a NotificationProvider');
    }
    return context;
};
